import { useCallback, type Dispatch } from 'react';
import {
  detectMaxRegression,
  type CurrentMax,
  type Exercise,
  type PRsByExercise,
} from '@jackedlogs/shared';

import { willPromptAmrap } from './amrapPromptRule';
import { detectSetPr, priorSessionSetsForPr } from './prDetect';
import { placeCompletedSetAtTail } from './timelinePlacement';
import {
  resolveRestSeconds,
  resolveRestTransition,
  type RestTransition,
} from './restTransition';
import type { UseRestTimer } from './useRestTimer';
import type { DraftEntry, LoggerAction } from './state/loggerDraftTypes';
import type { LoggerHostDeps } from './hostDeps';

// Per-set pill cycle orchestration extracted from LoggerScreen (Track
// R). The pending→done leg pre-computes the PR cue (detectSetPr, pure +
// unit-tested), records the inbox-74017a4d Sentry diagnostics, dispatches
// the cycle_set action and fires the rest timer; the other legs just
// dispatch (the reducer clears prFlash). Pre-compute happens here because
// the reducer is a pure state machine that can't reach the screen-level
// records fetch. Falls back to no PR cue when prHistory hasn't hydrated
// yet rather than blocking the press (would feel broken on cold gym 5G).
export function useSetCyclePill(args: {
  entries: readonly DraftEntry[];
  dispatch: Dispatch<LoggerAction>;
  prHistory: PRsByExercise | null;
  /** Stored MAX + setAt per exercise (from MaxesContext.maxMeta). Drives
   *  the bidirectional MAX-regression ⬇️ marker (next/coaching.md §9).
   *  null until the maxes map has loaded — the regression check then
   *  simply doesn't fire (no false ⬇️ against an empty map), same defence
   *  as prHistory. */
  currentMaxes: Map<string, CurrentMax> | null;
  catalog: Exercise[] | null;
  defaultRestSec: number | null | undefined;
  restTimer: UseRestTimer;
  /** Session start, used to anchor in-window placement when editing a
   *  past session. */
  startedAt: string;
  /** True when the Logger is editing a PAST saved session (edit mode,
   *  not repeat). Completing a set then places it in-window-at-tail
   *  instead of stamping the live clock (next/timeline.md). */
  isPastSessionEdit: boolean;
  /** Fired on the pending→done leg of a BODYWEIGHT set. The screen uses
   *  it to nudge the user (once) to register their bodyweight when
   *  Profile.bodyweightKg is unset — without it, bodyweight volume can't
   *  be computed (D1-S2, 2026-05-29). The guard + Alert live at the
   *  screen level; here we only signal the event. */
  onBodyweightSetCompleted?: () => void;
  sentryWorkoutContext?: LoggerHostDeps['sentryWorkoutContext'];
}) {
  const {
    entries,
    dispatch,
    sentryWorkoutContext,
    prHistory,
    currentMaxes,
    catalog,
    defaultRestSec,
    restTimer,
    startedAt,
    isPastSessionEdit,
    onBodyweightSetCompleted,
  } = args;
  return useCallback(
    // opts.rir (AP-0b): the effort meter's tap both WRITES the rir and
    // completes the set in one gesture. The caller queues the update_set
    // write before this dispatch, so the STATE lands right — but this
    // callback's `entries` closure is pre-dispatch, and the MAX-regression
    // check qualifies a maximal-effort set on rir === 0. Without the
    // override it would always read the PREVIOUS rir and miss the very tap
    // that says "nothing left". Returns { prFlash } so the meter can pulse
    // the tapped segment gold (the binding advances immediately after, so
    // the caller can't read it back off the set), and { restArmed } so the
    // caller knows whether the focus transition has a rest to sit through
    // (MX-5 slice 3b) or has to be applied at once.
    (
      entryIdx: number,
      setIdx: number,
      opts?: { rir?: number; transition?: RestTransition },
    ) => {
      const entry = entries[entryIdx];
      const setRow = entry?.sets[setIdx];
      if (!entry || !setRow) {
        dispatch({ type: 'cycle_set', entryIdx, setIdx });
        return { prFlash: false, restArmed: false };
      }
      // Only the pending→done transition runs the PR check; the other
      // legs clear prFlash inside the reducer regardless of what we
      // pass here.
      if (setRow.completedAt !== null) {
        dispatch({ type: 'cycle_set', entryIdx, setIdx });
        return { prFlash: false, restArmed: false };
      }
      const { built, isPr: prFlash } = detectSetPr({
        set: setRow,
        exerciseId: entry.exerciseId,
        isBodyweight: entry.isBodyweight,
        prHistory,
        // Candidate is still pending (completedAt null) → every set of
        // this exercise already completed in the session counts, so an
        // identical 2nd set doesn't re-flash a trophy the 1st owns.
        priorSessionSets: priorSessionSetsForPr(
          entries,
          entry.exerciseId,
          entry.isBodyweight,
          null,
        ),
      });
      // Bidirectional MAX (next/coaching.md §9): the DOWNWARD twin of the
      // PR check. Reuse the same `built` WorkoutSet — a qualifying
      // maximal-effort set (RIR 0 / fail) that computes ≥δ below a stale
      // stored MAX earns the red ⬇️. Single-set window: the per-set marker
      // is exactly detectMaxRegression over [built]. Doesn't fire until
      // maxes load (currentMaxes null), mirroring the prHistory guard.
      const currentMax = currentMaxes?.get(entry.exerciseId) ?? null;
      // buildWorkoutSetFromDraft carries no rir/fail (save-time payload adds
      // them later), so the ceiling qualification (rir === 0) must come from
      // the meter's opts.rir — attached here, on the set the check reads.
      const builtForCeiling =
        built !== null && typeof opts?.rir === 'number'
          ? { ...built, rir: opts.rir }
          : built;
      const regressionFlash =
        builtForCeiling !== null &&
        currentMax !== null &&
        detectMaxRegression({
          sets: [builtForCeiling],
          currentMax,
          nowMs: Date.now(),
        }) !== null;
      // Inbox 74017a4d diagnostic (v1.97.10): record every pending→done
      // PR-check input + result so the next session produces ground
      // truth on why the gold pill is missing. Per
      // feedback_diagnostics_before_fix_silent_native — ship diagnostics
      // first, then iterate the fix against real data. Breadcrumb runs
      // every tap; captureMessage only fires on the noteworthy paths
      // (PR detected, prHistory not yet loaded, buildWorkoutSetFromDraft
      // rejected) so the Sentry event volume stays low.
      const builtKind = built?.type ?? 'unbuildable';
      const builtWeight =
        built && built.type === 'weighted' ? built.weight : null;
      const builtReps = built && 'reps' in built ? built.reps : null;
      const prHistoryEntries = prHistory ? Object.keys(prHistory).length : 0;
      const bucketsForExercise = prHistory
        ? prHistory[entry.exerciseId] ?? null
        : null;
      const bucketSummary = bucketsForExercise
        ? Object.entries(bucketsForExercise)
            .slice(0, 8)
            .map(([k, v]) => `${k}:${v?.weightKg ?? '?'}`)
            .join(',')
        : null;
      sentryWorkoutContext?.breadcrumb('logger.cycleSetPill', {
        entryIdx,
        setIdx,
        exerciseSlug: entry.exerciseId,
        isBodyweight: entry.isBodyweight,
        prHistoryLoaded: prHistory !== null,
        prHistoryEntries,
        builtKind,
        builtWeight,
        builtReps,
        hasBucketsForExercise: bucketsForExercise !== null,
        bucketSummary,
        prFlash,
      });
      // 1.99.5: dropped the `logger.pr.fired` captureMessage. It was
      // a temporary diagnostic from 1.97.10 to confirm the PR pipeline
      // ever lit up; the pipeline is verified working now (Antenore's
      // 12×90 deficit-deadlift hit JACKEDLOGS-REACTNATIVE-9 as
      // intended) and routing every successful PR to a Sentry issue
      // is just noise. The breadcrumb above still captures the inputs
      // for any future regression. We keep the two warning paths
      // (no_history / unbuildable) since those indicate real problems.
      if (!prFlash && !prHistory) {
        sentryWorkoutContext?.breadcrumb('logger.pr.no_history_at_tap', {
          exerciseSlug: entry.exerciseId,
          prHistoryEntries: 0,
        });
      } else if (built === null) {
        sentryWorkoutContext?.breadcrumb('logger.pr.unbuildable_set', {
          exerciseSlug: entry.exerciseId,
          reps: setRow.reps,
          weight: setRow.weight,
          isBodyweight: entry.isBodyweight,
        });
      }
      // Past-session edit: place the set in-window-at-tail instead of
      // stamping the live clock (next/timeline.md). Live logging omits
      // completedAt so the reducer uses `now`.
      const completedAt = isPastSessionEdit
        ? placeCompletedSetAtTail(entries, startedAt)
        : undefined;
      dispatch({
        type: 'cycle_set',
        entryIdx,
        setIdx,
        prFlash,
        regressionFlash,
        ...(completedAt ? { completedAt } : {}),
      });
      // Whether a countdown actually started. Stays false on a past-session
      // edit and on a zero-length rest, and the caller reads it as "apply the
      // focus transition now, there is nothing to sit through".
      let restArmed = false;
      // Fire the rest timer on the pending→done leg — LIVE logging ONLY.
      // Editing a past saved session must NOT arm a rest countdown: the user
      // is correcting history, not training now, so a rest/execution cue is
      // out of place (Antenore 2026-07-10 — "in edit non dovrebbe partire il
      // timer del rest, cose che sono proprie di un logger e non di un
      // editor"). Same guard that already skips the bodyweight nudge below.
      if (!isPastSessionEdit) {
        // The rest length rides on the SAME "what comes next" answer that
        // moves the focus (MX-5 slice 3b): resolveRestTransition is asked once
        // by the caller and handed down, so the countdown and the transition
        // can't disagree. Without a caller-supplied one we resolve it here,
        // through the same function, with no group members.
        // ⚠️ DEFENSIVE AND CURRENTLY DEAD: the only live caller (the timeline
        // logger's commitSet) always supplies a transition, and the only path
        // that omits one is a past-session edit, which never reaches this block
        // at all. It is here so a future caller gets a rest rather than none —
        // but it would get the STANDALONE length even inside a superset. If you
        // add a caller, thread the transition through instead of trusting this
        // branch: it has no test because it has no user.
        const transition =
          opts?.transition ??
          resolveRestTransition({
            entries,
            entryIdx,
            loggedSetIdx: setIdx,
            groupMembers: [],
          });
        const ex = catalog?.find((x) => x.slug === entry.exerciseId);
        const seconds = resolveRestSeconds({
          transition,
          exerciseDefaultRestSec: ex?.defaultRestSec,
          profileDefaultRestSec: defaultRestSec,
        });
        // Stop any rest still running/paused before re-arming. start()
        // early-returns when seconds <= 0 (an exercise with
        // defaultRestSec = 0 means "no rest"), so without this the old
        // timer would keep ticking + its notification stay scheduled when
        // you complete a set on a zero-rest lift. For a normal restart
        // this is behaviour-neutral (reset cancels, start re-schedules).
        if (restTimer.state.endsAt !== null || restTimer.state.isPaused) {
          restTimer.reset();
        }
        // TM-12: a rest is armed at the end of EVERY set, including the one
        // that leaves nothing pending (`done`). The old guard skipped it —
        // inbox 1074fc37 — but "nothing pending" is not "I have finished": on a
        // one-exercise session the very first set is already `done`, and a set
        // can always be added. Save is the gesture that ends a workout, and
        // Save already resets the timer (useSaveWorkout, both legs). So the
        // only 0 left in the chain is the explicit per-exercise "no rest".
        // ⚠️ restArmed is what the caller uses to decide whether the focus
        // transition waits for a rest or fires immediately: it must stay TRUE
        // exactly when a countdown really started, or a set logged with no rest
        // would never advance.
        restArmed = seconds > 0;
        if (restArmed) {
          restTimer.start(seconds, entry.exerciseId);
        }
      }
      // Bodyweight set just completed during LIVE logging → let the screen
      // decide whether to nudge for a missing Profile.bodyweightKg
      // (once-only, non-blocking). Skipped on past-session edits: nagging
      // mid-edit of a month-old workout is disruptive and the weight may
      // not even apply to that date.
      // LG-27: and skipped when the AMRAP question is about to be raised for
      // this same set, because two interruptions on one tap is the bug. The
      // AMRAP question wins (his decision, 2026-08-12: it is about a
      // measurement that evaporates, the missing weight is not). ⭐ Standing
      // down does NOT consume the nudge — it is never shown, so
      // useBodyweightPrompt's once-per-session ref stays unspent and the next
      // bodyweight set without a competing question gets it. That is the
      // "waits its turn" half, and skipping it would silently swallow the
      // notice for the whole session.
      if (
        entry.isBodyweight &&
        !isPastSessionEdit &&
        !willPromptAmrap(entries, entryIdx, setIdx)
      ) {
        onBodyweightSetCompleted?.();
      }
      return { prFlash, restArmed };
    },
    // `sentryWorkoutContext` is host-injected and stable per mount (the RW-18
    // seam), and it is diagnostics — never an input to what this callback
    // computes — so re-creating the callback when it changes buys nothing.
    // CN-UI-LINT.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      entries,
      dispatch,
      prHistory,
      currentMaxes,
      catalog,
      restTimer,
      defaultRestSec,
      startedAt,
      isPastSessionEdit,
      onBodyweightSetCompleted,
    ],
  );
}
